import React, { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Bug, CheckCircle, ArrowLeft, ArrowRight, Trash, Eye } from "@phosphor-icons/react";
import ConfirmDialog from "../components/ConfirmDialog";
import EnrichmentPanel from "../components/EnrichmentPanel";

const PAGE_SIZE = 25;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-AR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function ErrorDetailsModal({ error, onClose }) {
  if (!error) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="error-details-modal">
      <div className="card-base p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted">{error.context || "Sin contexto"}</div>
            <h3 className="font-display font-black uppercase text-lg tracking-tight">{error.error_id}</h3>
          </div>
          <button onClick={onClose} className="btn-ghost text-muted hover:text-white" data-testid="error-modal-close">✕</button>
        </div>
        <div className="space-y-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Mensaje</div>
            <div className="font-mono text-xs bg-[#0A0A0A] border border-app p-2 whitespace-pre-wrap break-words">{error.message}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Fecha</div>
              <div className="text-xs">{fmtDate(error.created_at)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Usuario</div>
              <div className="text-xs">{error.user_name || "—"}{error.user_id ? ` (${error.user_id})` : ""}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">URL</div>
              <div className="text-xs truncate">{error.url || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">IP</div>
              <div className="text-xs">{error.ip || "—"}</div>
            </div>
          </div>
          {error.user_agent && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">User Agent</div>
              <div className="text-xs text-muted break-words">{error.user_agent}</div>
            </div>
          )}
          {error.stack && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Stack</div>
              <pre className="font-mono text-[10px] bg-[#0A0A0A] border border-app p-2 max-h-48 overflow-auto whitespace-pre-wrap break-words">{error.stack}</pre>
            </div>
          )}
          {error.component_stack && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Component Stack</div>
              <pre className="font-mono text-[10px] bg-[#0A0A0A] border border-app p-2 max-h-48 overflow-auto whitespace-pre-wrap break-words">{error.component_stack}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminErrors() {
  const [items, setItems] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [contextFilter, setContextFilter] = useState("");
  const [resolvedFilter, setResolvedFilter] = useState("open"); // open | resolved | all
  const [selected, setSelected] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (contextFilter) params.context = contextFilter;
      if (resolvedFilter === "open") params.resolved = false;
      else if (resolvedFilter === "resolved") params.resolved = true;
      const { data } = await api.get("/errors/reports", { params });
      setItems(data.items || []);
      setContexts(data.contexts || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [page, contextFilter, resolvedFilter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggleResolve = async (e) => {
    try {
      const { data } = await api.put(`/errors/${e.error_id}/resolve`);
      toast.success(data.resolved ? "Marcado como resuelto" : "Reabierto");
      setItems((arr) => arr.map((x) => x.error_id === e.error_id ? { ...x, resolved: data.resolved } : x));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (e) => {
    setConfirmTarget(e);
  };

  const performDelete = async () => {
    if (!confirmTarget) return;
    try {
      await api.delete(`/errors/${confirmTarget.error_id}`);
      toast.success("Reporte eliminado");
      setConfirmTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setConfirmTarget(null);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-5 sm:p-8" data-testid="admin-errors-page">
        <div className="mb-6 flex items-center gap-3">
          <Bug size={26} weight="duotone" className="text-primary-app" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[3px] text-muted">Observabilidad</div>
            <h1 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tighter">Log de errores</h1>
          </div>
        </div>

        <div className="mb-6">
          <EnrichmentPanel />
        </div>

        <div className="card-base p-5">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <select
              className="input-base !w-auto !py-2 text-sm"
              value={contextFilter}
              onChange={(e) => { setContextFilter(e.target.value); setPage(0); }}
              data-testid="errors-context-filter"
            >
              <option value="">Todos los contextos</option>
              {contexts.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              className="input-base !w-auto !py-2 text-sm"
              value={resolvedFilter}
              onChange={(e) => { setResolvedFilter(e.target.value); setPage(0); }}
              data-testid="errors-status-filter"
            >
              <option value="open">Abiertos</option>
              <option value="resolved">Resueltos</option>
              <option value="all">Todos</option>
            </select>
            <div className="text-xs text-muted ml-auto">
              {loading ? "Cargando..." : `${total} reporte${total === 1 ? "" : "s"}`}
            </div>
          </div>

          {items.length === 0 && !loading ? (
            <div className="text-muted py-12 text-center">Sin reportes en este filtro.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-app">
                    <th className="py-3 px-2 text-left text-[10px] uppercase tracking-widest text-muted">Fecha</th>
                    <th className="py-3 px-2 text-left text-[10px] uppercase tracking-widest text-muted">Contexto</th>
                    <th className="py-3 px-2 text-left text-[10px] uppercase tracking-widest text-muted">Usuario</th>
                    <th className="py-3 px-2 text-left text-[10px] uppercase tracking-widest text-muted">Mensaje</th>
                    <th className="py-3 px-2 text-center text-[10px] uppercase tracking-widest text-muted">Estado</th>
                    <th className="py-3 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e) => (
                    <tr key={e.error_id} className="border-b border-app hover:bg-surface-hover transition-colors" data-testid={`error-row-${e.error_id}`}>
                      <td className="py-3 px-2 text-xs text-muted font-mono whitespace-nowrap">{fmtDate(e.created_at)}</td>
                      <td className="py-3 px-2">
                        {e.context ? (
                          <span className="text-[10px] uppercase tracking-widest bg-[#1a1a1a] px-2 py-1 font-bold">{e.context}</span>
                        ) : <span className="text-muted text-xs">—</span>}
                      </td>
                      <td className="py-3 px-2 text-xs">{e.user_name || <span className="text-muted">Visitante</span>}</td>
                      <td className="py-3 px-2 text-xs max-w-[400px] truncate" title={e.message}>{e.message}</td>
                      <td className="py-3 px-2 text-center">
                        {e.resolved ? (
                          <span className="text-[10px] uppercase tracking-widest font-bold text-[#32D74B]">Resuelto</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest font-bold text-[#FFD60A]">Abierto</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right whitespace-nowrap">
                        <button onClick={() => setSelected(e)} className="btn-secondary !py-1.5 !px-2 text-xs inline-flex items-center mr-1" title="Ver detalle" data-testid={`error-view-${e.error_id}`}>
                          <Eye size={12} weight="bold" />
                        </button>
                        <button onClick={() => toggleResolve(e)} className="btn-secondary !py-1.5 !px-2 text-xs inline-flex items-center mr-1" title={e.resolved ? "Reabrir" : "Marcar resuelto"} data-testid={`error-resolve-${e.error_id}`}>
                          <CheckCircle size={12} weight={e.resolved ? "fill" : "bold"} className={e.resolved ? "text-[#32D74B]" : ""} />
                        </button>
                        <button onClick={() => remove(e)} className="btn-secondary !py-1.5 !px-2 text-xs inline-flex items-center hover:!border-[#FF3B30] hover:text-[#FF3B30]" title="Eliminar" data-testid={`error-delete-${e.error_id}`}>
                          <Trash size={12} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-5">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="errors-prev-page"
              >
                <ArrowLeft size={12} weight="bold" /> Anterior
              </button>
              <div className="text-xs text-muted">
                Página {page + 1} de {totalPages}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="errors-next-page"
              >
                Siguiente <ArrowRight size={12} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </div>

      <ErrorDetailsModal error={selected} onClose={() => setSelected(null)} />

      <ConfirmDialog
        open={!!confirmTarget}
        title="Eliminar reporte"
        body={confirmTarget ? `¿Estás seguro de eliminar ${confirmTarget.error_id}?\nEsta acción no se puede deshacer.` : ""}
        confirmLabel="Eliminar"
        danger
        onConfirm={performDelete}
        onCancel={() => setConfirmTarget(null)}
      />
    </Layout>
  );
}
